import { client } from "@/lib/hono";

export interface PublicListItem {
  id: string;
  name: string;
  formattedAddress: string;
  latitude: string;
  longitude: string;
  rating: string | null;
  priceLevel: number | null;
  types: string[];
  website: string | null;
  addedAt: string;
}

export interface PublicFeaturedList {
  id: string;
  listName: string;
  city?: string | null;
  createdAt: string;
  creatorName: string;
  items: PublicListItem[];
}

/** How a public list is addressed: the homepage uses the featured list's id, share links use a token. */
export type PublicListSource = { id: string } | { token: string };

const readList = async (response: Response): Promise<PublicFeaturedList> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as { error?: string }).error || "Failed to load list");
  }
  const result = (await response.json()) as { success: boolean; data?: PublicFeaturedList };
  if (!result.success || !result.data) throw new Error("Failed to load list");
  return result.data;
};

export const publicListsService = {
  async getFeaturedList(id: string): Promise<PublicFeaturedList> {
    return readList(await client.api.public.lists[":id"].$get({ param: { id } }));
  },
  async getListByToken(token: string): Promise<PublicFeaturedList> {
    return readList(await client.api.public.lists["by-token"][":token"].$get({ param: { token } }));
  },
  async get(source: PublicListSource): Promise<PublicFeaturedList> {
    return "token" in source ? this.getListByToken(source.token) : this.getFeaturedList(source.id);
  },
};
